/**
 * Token Refresh Service
 *
 * Handles auth token refresh.
 *
 * Architecture:
 * - Event-driven: Backend sends notification using ws when token < 10 min left
 * - On-demand refresh: Uses /api/v1/auth/ping/ endpoint
 * - Cookie-based: Updates HTTP-only cookies automatically
 * - WebSocket notification: Notifies WS when token is refreshed
 */

import { redirectToLogin } from '../utils/backendUrl';
import { API_CONFIG } from './apiConfig';
import { networkEventManager } from './networkEventManager';

const TOKEN_REFRESH_RETRY_ATTEMPTS = 3;

class TokenRefreshService {
    private isRefreshing: boolean = false;
    private lastRefreshTime: number = 0;
    private failureCount: number = 0;
    private onTokenRefreshedCallback: (() => void) | null = null;

    /**
     * Set callback to notify when token is refreshed
     * Used by websocketService to send token_refreshed message
     */
    setOnTokenRefreshedCallback(callback: () => void) {
        this.onTokenRefreshedCallback = callback;
    }

    /**
     * Trigger a token refresh
     * Called when WebSocket receives 'token_refresh_required' from backend
     * or on WebSocket reconnection to ensure valid session
     */
    async refresh(): Promise<boolean> {
        // Prevent concurrent refresh attempts
        if (this.isRefreshing) {
            console.log('[TokenRefresh] Already refreshing, skipping');
            return false;
        }

        // Rate limiting: Don't refresh more than once per 30 seconds
        const now = Date.now();
        if (now - this.lastRefreshTime < 30000) {
            console.log('[TokenRefresh] Skipping - refreshed less than 30s ago');
            return true; // Consider it success since we just refreshed
        }

        this.isRefreshing = true;
        console.log('[TokenRefresh] Refreshing token...');

        try {
            // Call auth ping endpoint to refresh token and update cookie
            const response = await fetch(`${API_CONFIG.BASE_URL}/auth/ping/`, {
                method: 'GET',
                credentials: 'include', // Include cookies
            });

            if (response.ok || response.status === 204) {
                this.lastRefreshTime = Date.now();
                this.failureCount = 0;

                // Notify callback (websocketService will send token_refreshed message)
                if (this.onTokenRefreshedCallback) {
                    this.onTokenRefreshedCallback();
                }

                console.log('[TokenRefresh] Token refreshed successfully');
                return true;
            } else if (response.status === 401 || response.status === 403) {
                // Auth failed - handle with retry logic
                this.failureCount++;
                
                if (this.failureCount >= TOKEN_REFRESH_RETRY_ATTEMPTS) {
                    console.error('[TokenRefresh] Auth failed after retries, redirecting to login');
                    networkEventManager.emitAuthFailure('Session expired during token refresh');
                    redirectToLogin();
                    return false;
                }
                
                // Retry after a short delay
                console.warn(`[TokenRefresh] Got ${response.status}, retry ${this.failureCount}/${TOKEN_REFRESH_RETRY_ATTEMPTS}`);
                this.isRefreshing = false;
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.refresh();
            } else {
                // Other error - increment failure count
                this.failureCount++;
                console.error(`[TokenRefresh] Unexpected status: ${response.status}`);

                if (this.failureCount >= TOKEN_REFRESH_RETRY_ATTEMPTS) {
                    console.error('[TokenRefresh] Too many failures');
                    networkEventManager.emitNetworkError('Token refresh failed', { status: response.status });
                }

                return false;
            }
        } catch (error) {
            console.error('[TokenRefresh] Network error:', error);
            this.failureCount++;

            if (this.failureCount >= TOKEN_REFRESH_RETRY_ATTEMPTS) {
                networkEventManager.emitNetworkError('Token refresh network error', error);
            }

            return false;
        } finally {
            this.isRefreshing = false;
        }
    }

    /**
     * Reset failure count
     * Call on successful WebSocket connection
     */
    reset() {
        this.failureCount = 0;
    }

    /**
     * Get refresh status for debugging
     */
    getStatus() {
        return {
            isRefreshing: this.isRefreshing,
            lastRefreshTime: this.lastRefreshTime,
            timeSinceLastRefresh: this.lastRefreshTime ? Date.now() - this.lastRefreshTime : null,
            failureCount: this.failureCount,
        };
    }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();

// Expose for debugging in development
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
    (window as any).tokenRefreshService = tokenRefreshService;
}
